import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ImageUploader from '../components/ImageUploader';
import FaqCategoriesManager from './faq/FaqCategoriesManager';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyForm = {
  question_ru: '',
  question_en: '',
  answer_ru: '',
  answer_en: '',
  category_key: '',
  subcategory_key: '',
  order: 0,
  is_active: true,
  image_url: '',
  video_url: ''
};

const FAQManagement = () => {
  const [activeTab, setActiveTab] = useState('questions'); // 'questions' | 'categories'
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [notification, setNotification] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [faqRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/faqs`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/faq-categories`, { headers: getAuthHeaders() }),
      ]);
      setFaqs(faqRes.data || []);
      setCategories(catRes.data || []);
    } catch (error) {
      console.error('Error loading FAQ data:', error);
      showNotification('Ошибка загрузки данных FAQ', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const topCategories = categories.filter((c) => !c.parent_key).sort((a, b) => a.order - b.order);
  const subsOf = (parentKey) =>
    categories.filter((c) => c.parent_key === parentKey).sort((a, b) => a.order - b.order);
  const catName = (key) => categories.find((c) => c.key === key)?.name_ru || key || '—';

  const handleOpenModal = (faq = null) => {
    if (faq) {
      setEditingFAQ(faq);
      setFormData({
        question_ru: faq.question_ru || '',
        question_en: faq.question_en || '',
        answer_ru: faq.answer_ru || '',
        answer_en: faq.answer_en || '',
        category_key: faq.category_key || '',
        subcategory_key: faq.subcategory_key || '',
        order: faq.order || 0,
        is_active: faq.is_active,
        image_url: faq.image_url || '',
        video_url: faq.video_url || ''
      });
    } else {
      setEditingFAQ(null);
      setFormData({ ...emptyForm, order: faqs.length });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFAQ(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // Reset subcategory if category changes
      if (name === 'category_key') next.subcategory_key = '';
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (editingFAQ) {
        await axios.put(`${API_URL}/api/admin/faqs/${editingFAQ.id}`, payload, { headers: getAuthHeaders() });
        showNotification('FAQ успешно обновлён');
      } else {
        await axios.post(`${API_URL}/api/admin/faqs`, payload, { headers: getAuthHeaders() });
        showNotification('FAQ успешно создан');
      }
      handleCloseModal();
      loadAll();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      showNotification('Ошибка сохранения FAQ', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить этот вопрос?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/faqs/${id}`, { headers: getAuthHeaders() });
      showNotification('FAQ успешно удалён');
      loadAll();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      showNotification('Ошибка удаления FAQ', 'error');
    }
  };

  const visibleFaqs = filterCategory === 'all' ? faqs : faqs.filter((f) => f.category_key === filterCategory);

  if (loading) {
    return <div className="text-center py-12">Загрузка...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Управление FAQ</h1>
          <p className="text-gray-600 mt-2">Часто задаваемые вопросы и категории</p>
        </div>
        {activeTab === 'questions' && (
          <button
            onClick={() => handleOpenModal()}
            data-testid="admin-faq-add-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition"
          >
            + Добавить вопрос
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('questions')}
          data-testid="admin-faq-tab-questions"
          className={`px-4 py-2 font-medium transition border-b-2 -mb-px ${activeTab === 'questions' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Вопросы ({faqs.length})
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          data-testid="admin-faq-tab-categories"
          className={`px-4 py-2 font-medium transition border-b-2 -mb-px ${activeTab === 'categories' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Категории ({categories.length})
        </button>
      </div>

      {notification && (
        <div className={`p-4 rounded-lg ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      {activeTab === 'categories' ? (
        <FaqCategoriesManager
          categories={categories}
          onChanged={loadAll}
          notify={showNotification}
        />
      ) : (
        <>
          {/* Category filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterCategory('all')}
              className={`px-3 py-1.5 rounded-full text-sm transition ${filterCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Все ({faqs.length})
            </button>
            {topCategories.map((cat) => {
              const count = faqs.filter((f) => f.category_key === cat.key).length;
              return (
                <button
                  key={cat.key}
                  onClick={() => setFilterCategory(cat.key)}
                  className={`px-3 py-1.5 rounded-full text-sm transition ${filterCategory === cat.key ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {cat.name_ru} ({count})
                </button>
              );
            })}
          </div>

          <div className="bg-white rounded-lg shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Порядок</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Вопрос (RU)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Категория</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleFaqs.map((faq) => (
                  <tr key={faq.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{faq.order}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{faq.question_ru}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {catName(faq.category_key)}
                      {faq.subcategory_key ? <span className="text-gray-400"> › {catName(faq.subcategory_key)}</span> : null}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${faq.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {faq.is_active ? 'Активен' : 'Неактивен'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                      <button onClick={() => handleOpenModal(faq)} className="text-blue-600 hover:text-blue-800">Редактировать</button>
                      <button onClick={() => handleDelete(faq.id)} className="text-red-600 hover:text-red-800">Удалить</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">{editingFAQ ? 'Редактировать FAQ' : 'Создать FAQ'}</h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Вопрос (RU) *</label>
                  <textarea name="question_ru" value={formData.question_ru} onChange={handleInputChange} rows={3} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Вопрос (EN) *</label>
                  <textarea name="question_en" value={formData.question_en} onChange={handleInputChange} rows={3} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>

              {/* Category / Subcategory */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Категория</label>
                  <select name="category_key" value={formData.category_key} onChange={handleInputChange} data-testid="admin-faq-category-select" className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 bg-white">
                    <option value="">— Без категории —</option>
                    {topCategories.map((cat) => (
                      <option key={cat.key} value={cat.key}>{cat.name_ru}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Подкатегория</label>
                  <select name="subcategory_key" value={formData.subcategory_key} onChange={handleInputChange} disabled={!formData.category_key || subsOf(formData.category_key).length === 0} data-testid="admin-faq-subcategory-select" className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 bg-white disabled:bg-gray-100">
                    <option value="">— Без подкатегории —</option>
                    {subsOf(formData.category_key).map((sub) => (
                      <option key={sub.key} value={sub.key}>{sub.name_ru}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                💡 Чтобы вставить ссылку в ответ, используйте формат <code className="bg-white px-1 rounded">[текст ссылки](/services/slug-услуги)</code> для внутренних страниц или <code className="bg-white px-1 rounded">[текст](https://...)</code> для внешних. Ссылки автоматически отрендерятся на сайте.
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ответ (RU) *</label>
                  <textarea name="answer_ru" value={formData.answer_ru} onChange={handleInputChange} rows={6} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 font-mono text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ответ (EN) *</label>
                  <textarea name="answer_en" value={formData.answer_en} onChange={handleInputChange} rows={6} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 font-mono text-sm" />
                </div>
              </div>

              {/* Media fields */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Медиафайлы (необязательно)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <ImageUploader value={formData.image_url} onChange={(url) => setFormData({ ...formData, image_url: url })} label="Изображение" maxSize={10} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">URL видео</label>
                    <input type="url" name="video_url" value={formData.video_url} onChange={handleInputChange} placeholder="https://youtube.com/watch?v=..." className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                    <p className="text-xs text-gray-500 mt-1">Ссылка на YouTube, Vimeo и т.д.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Порядок</label>
                  <input type="number" name="order" value={formData.order} onChange={handleInputChange} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
                <div className="flex items-center pt-8">
                  <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                  <label className="ml-2 block text-sm text-gray-900">Активен</label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button type="button" onClick={handleCloseModal} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Отмена</button>
                <button type="submit" data-testid="admin-faq-save-btn" className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg">{editingFAQ ? 'Сохранить' : 'Создать'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQManagement;
